import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonSpinner,
  IonText,
  IonChip,
  IonIcon,
  IonInput,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
} from '@ionic/react';
import { closeOutline, ticketOutline, checkmarkCircleOutline } from 'ionicons/icons';
import { ticketService, fulfillmentService } from '../services/api';
import { Ticket, Sale } from '../types';
import { getCurrencySymbol } from '../utils/currencyConstants';
import { SPLIT_TYPE_OPTIONS } from '../utils/ticketConstants';
import { formatSplitDisplay } from '../utils/formatUtils';
import { eventBus, EVENTS } from '../utils/eventBus';

interface FulfillmentModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  sale: Sale | null;
  onSuccess: () => void;
}

const FulfillmentModal: React.FC<FulfillmentModalProps> = ({
  isOpen,
  onDismiss,
  sale,
  onSuccess,
}) => {
  const [availableTickets, setAvailableTickets] = useState<Ticket[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fulfillmentDetails, setFulfillmentDetails] = useState<any>(null);

  useEffect(() => {
    if (isOpen && sale) {
      loadAvailableTickets();
      loadFulfillmentDetails();
    }
  }, [isOpen, sale]);

  const loadAvailableTickets = async () => {
    if (!sale || !sale.eventId) return;

    try {
      setLoading(true);
      const eventId = typeof sale.eventId === 'string' ? sale.eventId : sale.eventId._id;
      const tickets = await ticketService.getByEvent(eventId);
      
      // Filter for available tickets
      const available = tickets.filter(ticket => {
        const availableQty = ticket.quantity - (ticket.soldQuantity || 0);
        return availableQty > 0;
      });

      // Sort tickets by assignment efficiency for better user guidance
      // Note: We'll re-sort after fulfillment details are loaded
      const sortedTickets = available.sort((a, b) => {
        const aAvailable = a.quantity - (a.soldQuantity || 0);
        const bAvailable = b.quantity - (b.soldQuantity || 0);
        const neededQty = sale?.quantity || 1; // Use sale quantity as initial estimate
        
        // Priority 1: Category matches
        const aCategoryMatch = a.category === sale?.category ? 1 : 0;
        const bCategoryMatch = b.category === sale?.category ? 1 : 0;
        if (aCategoryMatch !== bCategoryMatch) return bCategoryMatch - aCategoryMatch;
        
        // Priority 2: Exact matches (perfect fit)
        const aExactMatch = aAvailable === neededQty ? 1 : 0;
        const bExactMatch = bAvailable === neededQty ? 1 : 0;
        if (aExactMatch !== bExactMatch) return bExactMatch - aExactMatch;
        
        // Priority 3: Minimal waste (prefer tickets that can fulfill the sale with minimal leftover)
        const aWaste = aAvailable >= neededQty ? aAvailable - neededQty : Infinity;
        const bWaste = bAvailable >= neededQty ? bAvailable - neededQty : Infinity;
        if (aWaste !== bWaste) return aWaste - bWaste;
        
        // Priority 4: Larger quantities first (prefer single large block over multiple small ones)
        return bAvailable - aAvailable;
      });
      
      setAvailableTickets(sortedTickets);
    } catch (err: any) {
      setError(err.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadFulfillmentDetails = async () => {
    if (!sale || !sale._id) return;

    try {
      const details = await fulfillmentService.getSaleFulfillment(sale._id);
      setFulfillmentDetails(details);
      
      // Set default quantity to remaining quantity needed
      const remainingQty = details?.remainingToFulfill || (sale?.quantity || 1);
      setQuantity(remainingQty);
      
      // Re-sort tickets with accurate remaining quantity
      if (availableTickets.length > 0) {
        const resortedTickets = [...availableTickets].sort((a, b) => {
          const aAvailable = a.quantity - (a.soldQuantity || 0);
          const bAvailable = b.quantity - (b.soldQuantity || 0);
          const neededQty = remainingQty;
          
          // Priority 1: Category matches
          const aCategoryMatch = a.category === sale?.category ? 1 : 0;
          const bCategoryMatch = b.category === sale?.category ? 1 : 0;
          if (aCategoryMatch !== bCategoryMatch) return bCategoryMatch - aCategoryMatch;
          
          // Priority 2: Exact matches (perfect fit)
          const aExactMatch = aAvailable === neededQty ? 1 : 0;
          const bExactMatch = bAvailable === neededQty ? 1 : 0;
          if (aExactMatch !== bExactMatch) return bExactMatch - aExactMatch;
          
          // Priority 3: Minimal waste (prefer tickets that can fulfill the sale with minimal leftover)
          const aWaste = aAvailable >= neededQty ? aAvailable - neededQty : Infinity;
          const bWaste = bAvailable >= neededQty ? bAvailable - neededQty : Infinity;
          if (aWaste !== bWaste) return aWaste - bWaste;
          
          // Priority 4: Larger quantities first (prefer single large block over multiple small ones)
          return bAvailable - aAvailable;
        });
        setAvailableTickets(resortedTickets);
      }
    } catch (err) {
      console.error('Failed to load fulfillment details:', err);
      // If error, default to sale quantity
      setQuantity(sale?.quantity || 1);
    }
  };

  const handleLink = async () => {
    if (!selectedTicketId || !sale) return;

    try {
      setLoading(true);
      setError(null);
      
      const result = await fulfillmentService.linkSaleToInventory(
        sale._id || sale.id || '',
        selectedTicketId,
        quantity
      );

      onSuccess();
      onDismiss();
      
      // Notify inventory to refresh
      eventBus.emit(EVENTS.REFRESH_TICKETS);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to link inventory');
    } finally {
      setLoading(false);
    }
  };

  const selectedTicket = availableTickets.find(t => (t._id || t.id) === selectedTicketId);
  const selectedTicketAvailable = selectedTicket ? 
    selectedTicket.quantity - (selectedTicket.soldQuantity || 0) : 0;

  // Calculate remaining quantity to fulfill
  const remainingToFulfill = fulfillmentDetails?.remainingToFulfill || (sale?.quantity || 1);

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Link Sale to Inventory</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onDismiss}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        {loading && !availableTickets.length ? (
          <div className="flex justify-center py-8">
            <IonSpinner />
          </div>
        ) : (
          <>
            {/* Assignment Guide */}
            <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg mb-4">
              <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">💡 Assignment Tips</h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                The ⭐ RECOMMENDED option minimizes inventory splitting. For example, when fulfilling 3 tickets, 
                taking 3 from a group of 4 (leaving 1) is better than taking a pair + single.
              </p>
            </div>

            {/* Sale Details */}
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Sale Details</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div className="space-y-2">
                  <p><strong>Buyer:</strong> {sale?.buyerName}</p>
                  <p><strong>Total Quantity Needed:</strong> {sale?.quantity || 1} tickets</p>
                  <p><strong>Category:</strong> {sale?.category || 'Any'}</p>
                  <p><strong>Split:</strong> {formatSplitDisplay(sale?.split || 'together')}</p>
                  {fulfillmentDetails && (
                    <>
                      <p className="text-green-600 dark:text-green-400">
                        <strong>Already Fulfilled:</strong> {fulfillmentDetails.totalFulfilled} tickets
                      </p>
                      <p className="text-red-600 dark:text-red-400">
                        <strong>Still Need:</strong> {remainingToFulfill} tickets
                      </p>
                    </>
                  )}
                </div>
              </IonCardContent>
            </IonCard>

            {error && (
              <div className="bg-red-100 dark:bg-red-900/30 p-4 rounded-lg mb-4">
                <IonText color="danger">{error}</IonText>
              </div>
            )}

            {remainingToFulfill <= 0 ? (
              <div className="text-center py-8">
                <IonIcon icon={checkmarkCircleOutline} className="text-4xl text-green-600" />
                <p className="mt-2 text-green-600">This sale is already fully fulfilled!</p>
              </div>
            ) : (
              <>
                {/* Quantity Input */}
                <IonItem className="mb-4">
                  <IonLabel position="stacked">Quantity to Link</IonLabel>
                  <IonInput
                    type="number"
                    value={quantity}
                    onIonChange={(e) => setQuantity(parseInt(e.detail.value!) || 1)}
                    min={1}
                    max={Math.min(remainingToFulfill, selectedTicketAvailable)}
                  />
                </IonItem>

                {/* Available Tickets */}
                <h3 className="text-lg font-medium mb-2">Available Inventory</h3>
                {availableTickets.length === 0 ? (
                  <p className="text-gray-500">No available inventory for this event</p>
                ) : (
                  <IonRadioGroup value={selectedTicketId} onIonChange={(e) => setSelectedTicketId(e.detail.value)}>
                    <IonList>
                      {availableTickets.map((ticket, index) => {
                        const ticketId = ticket._id || ticket.id || '';
                        const available = ticket.quantity - (ticket.soldQuantity || 0);
                        const isCategoryMatch = ticket.category === sale?.category;
                        const isExactMatch = available === remainingToFulfill;
                        const canFulfillFully = available >= remainingToFulfill;
                        const waste = canFulfillFully ? available - remainingToFulfill : 0;
                        const isOptimal = index === 0; // First item after sorting is most optimal
                        
                        // Generate efficiency indicator
                        const getEfficiencyInfo = () => {
                          if (isExactMatch) {
                            return { label: 'Perfect Fit', color: 'success', icon: checkmarkCircleOutline };
                          } else if (canFulfillFully && waste === 1) {
                            return { label: `Will leave 1 single`, color: 'warning', icon: ticketOutline };
                          } else if (canFulfillFully && waste <= 3) {
                            return { label: `Will leave ${waste} tickets`, color: 'medium', icon: ticketOutline };
                          } else if (canFulfillFully) {
                            return { label: `Will leave ${waste} tickets`, color: 'dark', icon: ticketOutline };
                          } else {
                            return { label: `Partial (${available} of ${remainingToFulfill})`, color: 'danger', icon: ticketOutline };
                          }
                        };
                        
                        const efficiency = getEfficiencyInfo();
                        
                        return (
                          <IonItem key={ticketId} className={isOptimal ? 'border-l-4 border-green-500 bg-green-50 dark:bg-green-900/20' : ''}>
                            <IonRadio slot="start" value={ticketId} />
                            <IonLabel>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium">
                                    {ticket.category} - {ticket.section || 'General'}
                                    {ticket.block && ` - Block ${ticket.block}`}
                                    {isOptimal && <span className="ml-2 text-green-600 text-sm font-bold">⭐ RECOMMENDED</span>}
                                  </h4>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <IonChip color="primary">
                                      <IonIcon icon={ticketOutline} />
                                      <IonLabel>{available} available</IonLabel>
                                    </IonChip>
                                    <IonChip color={efficiency.color}>
                                      <IonIcon icon={efficiency.icon} />
                                      <IonLabel>{efficiency.label}</IonLabel>
                                    </IonChip>
                                    <span className="text-sm">
                                      {getCurrencySymbol(ticket.currency || 'GBP')}
                                      {ticket.purchasePrice}/ticket
                                    </span>
                                    <span className="text-sm text-gray-500">
                                      Split: {formatSplitDisplay(ticket.split || 'together')}
                                    </span>
                                    {!isCategoryMatch && (
                                      <IonChip color="warning">
                                        <IonLabel>Different Category</IonLabel>
                                      </IonChip>
                                    )}
                                  </div>
                                  {ticket.restrictions && (
                                    <p className="text-sm text-gray-500 mt-1">
                                      Restrictions: {ticket.restrictions}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </IonLabel>
                          </IonItem>
                        );
                      })}
                    </IonList>
                  </IonRadioGroup>
                )}

                {/* Quantity Input */}
                {selectedTicketId && (
                  <IonItem className="mt-4">
                    <IonLabel>Quantity to Link:</IonLabel>
                    <IonInput
                      type="number"
                      value={quantity}
                      onIonChange={(e) => setQuantity(parseInt(e.detail.value!) || 1)}
                      min={1}
                      max={Math.min(remainingToFulfill, selectedTicketAvailable)}
                      className="text-right"
                    />
                  </IonItem>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 mt-4">
                  <IonButton 
                    expand="block" 
                    onClick={handleLink}
                    disabled={!selectedTicketId || loading || quantity > selectedTicketAvailable}
                  >
                    {loading ? <IonSpinner /> : `Link ${quantity} Ticket${quantity > 1 ? 's' : ''} to Sale`}
                  </IonButton>
                  <IonButton 
                    expand="block" 
                    fill="outline" 
                    onClick={onDismiss}
                  >
                    Cancel
                  </IonButton>
                </div>
              </>
            )}
          </>
        )}
      </IonContent>
    </IonModal>
  );
};

export default FulfillmentModal;